import { estimateGas, waitForTransactionReceipt } from "viem/actions";
import { useWriteContract as _useWriteContract, useAccount, useClient } from "wagmi";
import { ABS_CHAIN } from "~/core/constants";
import { SESSION_KEYS, useAbsSession, WriteContractFunctionParametersWithSessionKey } from "~/core/contexts/AbsSessionContext";
import { reservePopup, releaseReservedPopup } from "@privy-io/popup";
import { useIsPrivyCrossApp } from "./useIsPrivyCrossApp";
import { encodeFunctionData, Hex } from "viem";

// Attaches a no-op rejection handler so a deferred receipt promise never surfaces
// as an unhandled rejection while still rejecting for any later awaiter.
function markPromiseHandled<T>(promise: Promise<T>): Promise<T> {
  void promise.catch(() => {})
  return promise
}

export const useWriteContract = () => {
  const { address } = useAccount()
  const { chainId } = useAccount()
  const { writeContractAsync: defaultWriteContractAsync } = _useWriteContract();
  const { writeContractAsync: absSessionWriteContractAsync } = useAbsSession()
  const { isPrivyCrossApp } = useIsPrivyCrossApp()

  const client = useClient()

  const writeContractAsync = async (args: WriteContractFunctionParametersWithSessionKey, options?: { skipReservePopup?: boolean}) => {
    if (chainId !== ABS_CHAIN.id)
      throw new Error(`Unsupported network: ${chainId}`);

    if (!options?.skipReservePopup && isPrivyCrossApp && args.sessionKey === SESSION_KEYS.NONE) {
      reservePopup()
    }

    try {
      const tx = await _writeContractAsync(args)
      const receipt = await waitForTransactionReceipt(client!, { hash: tx });
      if (receipt.status !== 'success')
        throw new Error('Transaction Failed: ' + tx);
      return tx
    } catch (error) {
      throw error
    } finally {
      releaseReservedPopup()
    }
  }

  // Submits the tx and returns its hash immediately, without waiting for the
  // receipt. The returned receiptPromise resolves once mined (rejects on a
  // failed tx) so callers that need ordering (e.g. settlement after createGame)
  // can await it. Used by the optimistic play flow: submit tx, then fire the
  // play API with the hash instead of blocking on confirmation.
  const writeContractWithoutWait = async (
    args: WriteContractFunctionParametersWithSessionKey,
    options?: { skipReservePopup?: boolean },
  ): Promise<{ hash: Hex; receiptPromise: Promise<void> }> => {
    if (chainId !== ABS_CHAIN.id)
      throw new Error(`Unsupported network: ${chainId}`);

    if (!options?.skipReservePopup && isPrivyCrossApp && args.sessionKey === SESSION_KEYS.NONE) {
      reservePopup()
    }

    try {
      const hash = await _writeContractAsync(args)
      const receiptPromise = markPromiseHandled(
        waitForTransactionReceipt(client!, { hash }).then((receipt) => {
          if (receipt.status !== 'success')
            throw new Error('Transaction Failed: ' + hash);
        }),
      )
      return { hash, receiptPromise }
    } finally {
      releaseReservedPopup()
    }
  }

  const _writeContractAsync = async (args: WriteContractFunctionParametersWithSessionKey) => {
    if (isPrivyCrossApp && args.sessionKey !== SESSION_KEYS.NONE)
      return absSessionWriteContractAsync(args)

    await estimateGas(client!, {
      account: address!,
      to: args.address,
      data: encodeFunctionData(args),
      value: args.value ?? 0n,
    })

    return defaultWriteContractAsync(args)
  }

  return {
    writeContractAsync,
    writeContractWithoutWait,
  }
}